import { setTimeout } from "node:timers/promises";
import { useEffect } from "react";
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  UserButton,
  useUser,
} from "@clerk/react-router";
import { rootAuthLoader } from "@clerk/react-router/ssr.server";
import { dark } from "@clerk/themes";
import { invariant } from "@epic-web/invariant";
import { Analytics } from "@vercel/analytics/react";
import { setDefaultOptions } from "date-fns";
import { fr } from "date-fns/locale";
import { enCA } from "date-fns/locale/en-CA";
import { Globe } from "lucide-react";
import posthog from "posthog-js";
import { useTranslation } from "react-i18next";
import {
  data,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRevalidator,
  useRouteError,
} from "react-router";
import { useChangeLanguage } from "remix-i18next/react";
import { getToast } from "remix-toast";

import { clerk } from "@acme/backend/clerk";
import { contextUtils } from "@acme/domain-utils";
import { getFxRates } from "@acme/domain-utils/features";
import { useNonce, useToast } from "@acme/hooks";
import { ClientHintCheck, getHints } from "@acme/remix/hints";
import { i18nextServer } from "@acme/remix/i18n.server";
import { typedUserPreferencesCookie } from "@acme/remix/settings.server";
import { getTheme, ThemeSwitch, useTheme } from "@acme/remix/theme";
import { getCurrencyFromIp } from "@acme/remix/utils";
import { cn } from "@acme/ui";
import { buttonVariants } from "@acme/ui/button";
import { Separator } from "@acme/ui/separator";
import { GlobalError } from "@acme/ui/shared/global-error";
import { Skeleton } from "@acme/ui/skeleton";
import { Toaster } from "@acme/ui/sonner";
import { TooltipProvider } from "@acme/ui/tooltip";

import { DebugInfo } from "~/components/DebugInfo";
import { useRootLoaderData } from "~/hooks/use-root-loader-data";
import { getUserId } from "~/lib/get-user-id";
import { logger } from "~/lib/pino.server";
import { appName } from "../package.json";
import type { Route } from "./+types/root";
import { env } from "./env";
import globalStyles from "./styles.css?url";

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: globalStyles },
];

export const meta = ({ data }: Route.MetaArgs) => {
  return [{ title: data?.meta.appName }];
};

export const handle = {
  // In the handle export, we can add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  // TIP: In most cases, you should set this to your defaultNS from your i18n config
  // or if you did not set one, set it to the i18next default namespace "translation"
  i18n: "translation",
};

export const loader = (_args: Route.LoaderArgs) => {
  return rootAuthLoader(
    _args,
    async (args) => {
      const { request } = args;
      if (env.ARTIFICIAL_SLOWDOWN_TIMEOUT_IN_MS) {
        await setTimeout(env.ARTIFICIAL_SLOWDOWN_TIMEOUT_IN_MS);
      }
      const hints = getHints(request);
      const timeZone = hints.timeZone;
      const locale = await i18nextServer.getLocale(request);
      const currency = await getCurrencyFromIp(args.request);
      const { userId, sessionClaims } = request.auth;
      if (userId) {
        invariant(sessionClaims, "Session claims metadata is required");
        if (
          !sessionClaims.metadata.locale ||
          !sessionClaims.metadata.currency ||
          !sessionClaims.metadata.timeZone ||
          (sessionClaims.metadata.autoDetectTimeZone &&
            sessionClaims.metadata.timeZone !== timeZone)
        ) {
          const metadata = {
            publicMetadata: {
              ...(!sessionClaims.metadata.locale ? { locale } : {}),
              ...(!sessionClaims.metadata.currency ? { currency } : {}),
              ...(!sessionClaims.metadata.timeZone ||
              sessionClaims.metadata.autoDetectTimeZone
                ? { timeZone }
                : {}),
            },
          };
          logger.info({ metadata }, "Updating user metadata");
          await clerk.users.updateUserMetadata(userId, metadata);
        }
      }

      const userPreferences = await typedUserPreferencesCookie.parse(
        request.headers.get("Cookie"),
      );
      const { toast, headers } = await getToast(request);

      const distinctId = await getUserId(args);

      const fxRates = await getFxRates(contextUtils);

      return data(
        {
          theme: getTheme(request),
          userId: distinctId,
          loggedIn: request.auth.userId,
          hasClaims: Boolean(sessionClaims?.metadata.locale),
          currency:
            sessionClaims?.metadata.currency ??
            userPreferences?.currency ??
            currency,
          timeZone:
            sessionClaims?.metadata.timeZone ??
            userPreferences?.timeZone ??
            timeZone,
          locale:
            sessionClaims?.metadata.locale ?? userPreferences?.locale ?? locale,
          sessionExpiresAt: request.auth.sessionId
            ? (await clerk.sessions.getSession(request.auth.sessionId)).expireAt
            : undefined,
          fxRates,
          pendingToast: toast,
          meta: {
            appLogo: "/input.svg",
            appName: "Ticket Notifier",
            appDescription:
              "Get notified when Resale Tickets fall below your desired price",
            frontendUrl: env.FRONTEND_URL,
          },
          userMetadata: sessionClaims?.metadata,
          hasAuth: true,
          requestInfo: {
            hints: getHints(args.request),
            path: new URL(request.url).pathname,
            userPrefs: {
              currency: userPreferences?.currency,
              theme: getTheme(request),
              timeZone: userPreferences?.timeZone,
              locale: userPreferences?.locale,
            },
          },
        },
        {
          headers,
        },
      );
    },
    {
      secretKey: env.CLERK_SECRET_KEY,
    },
  );
};

function App() {
  const loaderData = useRootLoaderData();
  const revalidator = useRevalidator();
  const { user } = useUser();
  const theme = useTheme();
  const nonce = useNonce();
  const { i18n } = useTranslation();

  useChangeLanguage(loaderData.locale);

  useEffect(() => {
    setDefaultOptions({ locale: i18n.language == "fr" ? fr : enCA });
  }, [loaderData.locale]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const distinctId = posthog.get_distinct_id?.();

  useEffect(() => {
    if (distinctId && loaderData.userId && distinctId !== loaderData.userId) {
      posthog.identify(loaderData.userId, {
        userId: loaderData.userId,
        timeZone: loaderData.userMetadata?.timeZone,
        locale: loaderData.userMetadata?.locale,
        currency: loaderData.userMetadata?.currency,
        email: user?.primaryEmailAddress?.emailAddress,
      });
    }
  }, []);

  // Hook to show the toasts
  useToast(loaderData);

  useEffect(() => {
    if (!loaderData.hasClaims) {
      void user?.reload().then(() => {
        void revalidator.revalidate();
      });
    }
  }, [loaderData.hasClaims, user]);

  return (
    <html lang={i18n.language || "en"} className={cn(theme)} dir={i18n.dir()}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content={loaderData.meta.appDescription} />
        <Meta />
        <ClientHintCheck nonce={nonce} />
        <Links />
      </head>
      <body className="bg-background min-h-screen font-sans antialiased">
        <div vaul-drawer-wrapper="">
          <div className="bg-background relative mx-auto flex min-h-screen max-w-screen-xl flex-col [--header-height:calc(theme(spacing.12))] sm:[--header-height:calc(theme(spacing.14))]">
            <TooltipProvider>
              <header className="border-border/40 bg-background/95 supports-[backdrop-filter]:bg-background/60 fixed top-0 z-50 flex w-full max-w-screen-xl justify-center border-b backdrop-blur-sm">
                <div className="relative flex h-[calc(var(--header-height)-1px)] w-full items-center justify-between gap-2 px-4">
                  <div>
                    <a
                      href={loaderData.meta.frontendUrl}
                      className="flex items-center hover:cursor-pointer"
                    >
                      <div
                        className={cn(
                          "font-geist hidden pr-1 text-2xl font-semibold tracking-tighter whitespace-nowrap sm:relative sm:block",
                        )}
                      >
                        <div>{appName}</div>
                        <div className="text-foreground/95 absolute right-0 -mt-0.5 pr-2 text-[9px] leading-[0.5rem] font-light tracking-wider">
                          BETA VERSION
                        </div>
                      </div>
                      <img
                        alt={appName}
                        src={loaderData.meta.appLogo}
                        className="h-8 w-8 antialiased dark:invert"
                      />
                    </a>
                    <Separator orientation="vertical" className="mx-4 h-8" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <ThemeSwitch
                      userPreference={loaderData.requestInfo.userPrefs.theme}
                    />
                    <SignedOut>
                      <Link
                        prefetch="intent"
                        className={cn(
                          buttonVariants({ variant: "link" }),
                          "pl-2",
                        )}
                        to="/sign-in"
                      >
                        Log in
                      </Link>
                      <Link
                        prefetch="intent"
                        className={buttonVariants({ variant: "default" })}
                        to="/sign-up"
                      >
                        Sign up
                      </Link>
                    </SignedOut>
                    <SignedIn>
                      <UserButton
                        fallback={<Skeleton className="size-7 rounded-full" />}
                        userProfileMode="navigation"
                        userProfileUrl={"/account"}
                        appearance={{
                          baseTheme: theme === "dark" ? dark : undefined,
                          elements: {
                            userButtonPopoverFooter: "hidden",
                            userButtonOuterIdentifier:
                              "hidden md:block text-primary",
                          },
                        }}
                      >
                        <UserButton.MenuItems>
                          <UserButton.Link
                            href="/settings"
                            label="Change currency / time zone"
                            labelIcon={<Globe className="size-3.5" />}
                          ></UserButton.Link>
                        </UserButton.MenuItems>
                      </UserButton>
                    </SignedIn>
                  </div>
                </div>
              </header>
              <main className="absolute top-[var(--header-height)] w-full flex-1">
                <Outlet />
              </main>
              <ScrollRestoration />
              <Scripts />
              <Toaster richColors closeButton theme={theme} />
              <Analytics />
              <footer className="flex w-full justify-center py-6">
                <div className="w-full max-w-screen-2xl px-4">
                  <p
                    className={cn(
                      "text-muted-foreground text-center text-sm leading-loose text-balance md:text-left",
                      !loaderData.hasClaims && "hidden",
                    )}
                  >
                    <Link
                      className="font-medium underline-offset-4 hover:underline"
                      to="mailto:alexandre@godard.me"
                      target="_blank"
                    >
                      Feedback / Report bug
                    </Link>
                  </p>
                  <div
                    className={
                      loaderData.hasClaims ? "hidden" : "black sm:hidden"
                    }
                  ></div>
                </div>
              </footer>

              {(loaderData.userMetadata?.role === "admin" ||
                import.meta.env.MODE === "development") && (
                <DebugInfo
                  sessionExpiresAt={loaderData.sessionExpiresAt}
                  locale={loaderData.locale}
                  timeZone={loaderData.timeZone}
                  currency={loaderData.currency}
                  userId={loaderData.userId}
                  distinctId={distinctId}
                />
              )}
            </TooltipProvider>
          </div>
        </div>
      </body>
    </html>
  );
}

function AppWithProviders({ loaderData }: { loaderData: Route.LoaderArgs }) {
  return (
    <ClerkProvider
      publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
      loaderData={loaderData}
    >
      <App />
    </ClerkProvider>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  return (
    <html>
      <head>
        <title>Oh no!</title>
        <Meta />
        <Links />
      </head>
      <body>
        <GlobalError appName={appName} />

        <Scripts />
      </body>
    </html>
  );
}

export default AppWithProviders;
